import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/public-pooja-account.css";

const API_BASE = "https://webcontrol.suvidham.in/api";

export default function PoojaLogin() {
  const navigate = useNavigate();

  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  // =========================================================
  // MOBILE NUMBER CHANGE
  // =========================================================

  const handleMobileChange = (event) => {
    const value = event.target.value
      .replace(/\D/g, "")
      .slice(0, 10);

    setMobile(value);
  };

  // =========================================================
  // LOGIN
  // =========================================================

  const login = async (event) => {
    event.preventDefault();

    setMessage("");

    // ---------------------------------------------------------
    // FRONTEND VALIDATION
    // ---------------------------------------------------------

    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setMessage("Please enter a valid 10 digit mobile number.");
      return;
    }

    if (!password) {
      setMessage("Please enter your password.");
      return;
    }

    setBusy(true);

    try {
      const response = await fetch(
        `${API_BASE}/public_login.php`,
        {
          method: "POST",

          headers: {
            "Content-Type": "text/plain;charset=UTF-8",
          },

          body: JSON.stringify({
            mobile,
            password,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || data.status !== "success") {
        throw new Error(
          data.message || "Unable to login. Please try again."
        );
      }

      // -------------------------------------------------------
      // STORE CUSTOMER SESSION
      // -------------------------------------------------------

      localStorage.setItem(
        "pooja_user_token",
        data.token
      );

      localStorage.setItem(
        "pooja_user",
        JSON.stringify(data.user)
      );

      // -------------------------------------------------------
      // REDIRECT TO CUSTOMER ACCOUNT
      // -------------------------------------------------------

      navigate("/my-pooja-bookings");
    } catch (error) {
      setMessage(
        error.message ||
          "Unable to login. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  // =========================================================
  // UI
  // =========================================================

  return (
    <div className="pa-login-page">
      <div className="pa-login-wrapper">
        {/* ===================================================
            LEFT SIDE
        =================================================== */}

        <div className="pa-login-welcome">
          <div className="pa-login-welcome-content">
            <span className="pa-login-small-title">
              ONLINE POOJA BOOKING
            </span>

            <h1>
              Welcome Back
            </h1>

            <p className="pa-login-description">
              Login to your devotee account to view your
              pooja bookings, payment status and account
              information.
            </p>

            <div className="pa-login-benefits">
              <div className="pa-login-benefit">
                <div className="pa-benefit-icon">
                  <i className="fa fa-calendar-check-o" />
                </div>

                <div>
                  <strong>Booking History</strong>

                  <span>
                    View all your previous pooja bookings.
                  </span>
                </div>
              </div>

              <div className="pa-login-benefit">
                <div className="pa-benefit-icon">
                  <i className="fa fa-credit-card" />
                </div>

                <div>
                  <strong>Payment Status</strong>

                  <span>
                    Check the payment status of your
                    bookings.
                  </span>
                </div>
              </div>

              <div className="pa-login-benefit">
                <div className="pa-benefit-icon">
                  <i className="fa fa-user" />
                </div>

                <div>
                  <strong>Devotee Information</strong>

                  <span>
                    Manage your contact and communication
                    details.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="pa-login-decoration pa-decoration-one" />
          <div className="pa-login-decoration pa-decoration-two" />
        </div>

        {/* ===================================================
            RIGHT SIDE - LOGIN
        =================================================== */}

        <div className="pa-login-form-side">
          <form
            className="pa-login-card"
            onSubmit={login}
            noValidate
          >
            {/* LOGIN ICON */}

            <div className="pa-login-icon">
              <i className="fa fa-user" />
            </div>

            {/* HEADING */}

            <div className="pa-login-heading">
              <span>DEVOTEE ACCOUNT</span>

              <h2>Login</h2>

              <p>
                Enter the mobile number and password you
                used while making your pooja booking.
              </p>
            </div>

            {/* ERROR */}

            {message && (
              <div className="pa-login-error">
                <i className="fa fa-exclamation-circle" />

                <span>{message}</span>

                <button
                  type="button"
                  onClick={() => setMessage("")}
                  aria-label="Close message"
                >
                  ×
                </button>
              </div>
            )}

            {/* ===============================================
                MOBILE NUMBER
            =============================================== */}

            <div className="pa-login-floating-field">
              <input
                id="pooja-login-mobile"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                maxLength="10"
                placeholder=" "
                value={mobile}
                disabled={busy}
                onChange={handleMobileChange}
              />

              <label htmlFor="pooja-login-mobile">
                Mobile Number
              </label>

              <i className="fa fa-phone" />
            </div>

            {/* ===============================================
                PASSWORD
            =============================================== */}

            <div className="pa-login-floating-field">
              <input
                id="pooja-login-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder=" "
                value={password}
                disabled={busy}
                onChange={(event) =>
                  setPassword(event.target.value)
                }
              />

              <label htmlFor="pooja-login-password">
                Password
              </label>

              <button
                type="button"
                className="pa-password-toggle"
                onClick={() =>
                  setShowPassword((current) => !current)
                }
                disabled={busy}
                aria-label={
                  showPassword
                    ? "Hide password"
                    : "Show password"
                }
                title={
                  showPassword
                    ? "Hide password"
                    : "Show password"
                }
              >
                <i
                  className={`fa ${
                    showPassword
                      ? "fa-eye-slash"
                      : "fa-eye"
                  }`}
                />
              </button>
            </div>

            {/* ===============================================
                LOGIN BUTTON
            =============================================== */}

            <button
              type="submit"
              className="pa-login-button"
              disabled={busy}
            >
              {busy ? (
                <>
                  <i className="fa fa-spinner fa-spin" />
                  Logging in...
                </>
              ) : (
                <>
                  <i className="fa fa-sign-in" />
                  Login to My Account
                </>
              )}
            </button>

            {/* ===============================================
                SECURITY INFORMATION
            =============================================== */}

            <div className="pa-login-security">
              <i className="fa fa-lock" />

              <p>
                Your account is protected. Please do not
                share your password with anyone.
              </p>
            </div>

            {/* ===============================================
                BACK TO BOOKING
            =============================================== */}

            <div className="pa-login-footer">
              <span>Want to make another pooja booking?</span>

              <button
                type="button"
                onClick={() =>
                  navigate("/online-pooja-booking")
                }
              >
                <i className="fa fa-arrow-left" />
                Go to Pooja Booking
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}